import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { Box, CircularProgress } from '@mui/material'
import EV91Loader from "./components/EV91Loader"; // ⬅️ ADD THIS
import SparePartsDashboard from "./modules/spare-parts/dashboard/Dashboard";
import { CategoriesPage } from "./modules/spare-parts/categories";
import { SuppliersPage } from "./modules/spare-parts/suppliers";
import { SparePartsPage } from "./modules/spare-parts/spare-parts";
import { InventoryPage as SparePartsInventoryPage } from "./modules/spare-parts/inventory";
import { PurchaseBillsPage } from "./modules/spare-parts/purchase-bills";
import { MaintenancePage as SparePartsMaintenancePage } from "./modules/spare-parts/maintenance";
import { PreventiveMaintenancePage as SparePartsPreventiveMaintenancePage } from "./modules/spare-parts/preventive-maintenance";
import { AnalyticsPage as SparePartsAnalyticsPage } from "./modules/spare-parts/analytics";
import { ReportsPage as SparePartsReportsPage } from "./modules/spare-parts/reports";
import { OperationsPage as SparePartsOperationsPage } from "./modules/spare-parts/operations";
import { MaintenanceAssignmentsPage } from "./modules/spare-parts/maintenance-assignments";

// Import pages
import Login from './pages/Login'
import Layout from './components/layout/Layout'

// Import Order Management pages - DISABLED
// import OrderList from './pages/OrderList'
// import OrderDetail from './pages/OrderDetail'
// import OrderForm from './pages/OrderForm'

// Vendor & Referral Management Pages
import VendorManagement from './pages/VendorManagement'
import TelecallerManagement from './pages/TelecallerManagement'
import ReferralTracking from './pages/ReferralTracking'
import CommissionApproval from './pages/CommissionApproval'
import VendorDashboard from './pages/VendorDashboard'

// Import auth components
import SignUpForm from './components/auth/SignUpForm'
import ForgotPasswordForm from './components/auth/ForgotPasswordForm'
import ResetPasswordForm from './components/auth/ResetPasswordForm'
import EmailVerificationPage from './components/auth/EmailVerificationPage'
import ResendVerificationForm from './components/auth/ResendVerificationForm'

import Dashboard from './pages/Dashboard'
import OperationsDashboard from './pages/OperationsDashboard'
import SalesDashboard from './pages/SalesDashboard'
import FinanceDashboard from './pages/FinanceDashboard'
import WarRoom from './pages/WarRoom'
import WarRoomCharts from "./pages/WarRoomCharts";
import DistanceAnalytics from './pages/DistanceAnalytics';
import ManagementDashboard from './pages/ManagementDashboard'
import SupplyDashboard from './pages/SupplyDashboard'
import InventoryDashboard from './pages/InventoryDashboard'
import Teams from './pages/Teams'
import CreateTeam from './pages/CreateTeam'
import EditTeam from './pages/EditTeam'
import ClientStoreManagement from './pages/ClientStoreManagement'
import RiderEarnings from './pages/RiderEarnings'
import ClientRiderMappingManagement from './pages/ClientRiderMappingManagement'
import RiderManagement from './pages/RiderManagement'
import RiderDetail from './pages/RiderDetail'
import RiderVehicleAnalytics from './pages/RiderVehicleAnalytics'
import RiderAttendanceAnalytics from './pages/RiderAttendanceAnalytics'
import RiderVehicleHistory from './pages/RiderVehicleHistory'
import RiderTickets from './pages/RiderTickets'
import BulkRiderImport from './pages/BulkRiderImport'
import BulkVehicleAssignmentImport from './pages/BulkVehicleAssignmentImport'
import MISManagement from './pages/MISManagement'
import MISBulkUploadPage from './pages/MISBulkUploadPage'
import MISHistorySummary from './pages/MISHistorySummary'
import Payouts from './pages/Payouts'
import PayoutsBulkUpload from './pages/PayoutsBulkUpload'
import ManualCollections from './pages/ManualCollections'
import DeficitDetails from './pages/DeficitDetails'


// Vehicle Management Pages
import VehicleDashboard from './pages/VehicleDashboard'
import VehicleInventory from './pages/VehicleInventory'
import DeploymentTicketsPage from './pages/DeploymentTicketsPage'
import ReturnTicketsPage from './pages/ReturnTicketsPage'
import VehicleStatusBackupReport from './pages/VehicleStatusBackupReport'
import VehicleForm from './pages/VehicleForm'
import VehicleProfile from './pages/VehicleProfile'
import DamageManagement from './pages/DamageManagement'
import DamageForm from './pages/DamageForm'
import BulkVehicleImport from './pages/BulkVehicleImport'
import BatteryInventory from './pages/BatteryInventory'
import ChargerInventory from './pages/ChargerInventory'

// OEM and Vehicle Model Management Pages
import OEMManagement from './pages/OEMManagement'
import OEMForm from './pages/OEMForm'
import VehicleModelManagement from './pages/VehicleModelManagement'
import VehicleModelForm from './pages/VehicleModelForm'

// Hub Management Pages
import HubManagement from './pages/HubManagement'
import HubForm from './pages/HubForm'
import HubDetails from './pages/HubDetails'

// City Management Pages
import CityManagement from './pages/CityManagement'
import CityForm from './pages/CityForm'
import CityHeadManagement from './pages/CityHeadManagement'
import FieldRecruiterManagement from './pages/FieldRecruiterManagement'
import CityDashboard from './pages/CityDashboard'

// Service Management Pages
import ServiceManagement from './pages/ServiceManagement'
import ServiceScheduleForm from './pages/ServiceScheduleForm'
// import UnifiedServiceDashboard from './pages/UnifiedServiceDashboard' // DISABLED - Unified Service Management

// Debug Components
import DebugVehicleAPI from './pages/DebugVehicleAPI'

// Order Management Pages
import OrderManagement from './pages/OrderManagement'
import OrderAnalytics from './pages/OrderAnalytics'
import OrderBulkUpload from './pages/OrderBulkUpload'

// Auth Management Pages
import Users from './pages/Users'
import Roles from './pages/Roles'
import Profile from './pages/Profile'
import Settings from './pages/Settings'
import WarRoomSummaryPage from './pages/WarRoomSummaryPage'
import APIDocumentation from './pages/APIDocumentation'

// Security Deposit Management Pages
import SecurityDepositConfigManagement from './pages/SecurityDepositConfigManagement'
import SecurityDepositManagement from './pages/SecurityDepositManagement'
import PayLinkTransactions from './pages/PayLinkTransactions'
import Refund from './pages/Refund'

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

// Public Route component (redirect if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>
}

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUpForm />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordForm />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPasswordForm />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email/:token"
        element={
          <PublicRoute>
            <EmailVerificationPage />
          </PublicRoute>
        }
      />
      <Route
        path="/resend-verification"
        element={
          <PublicRoute>
            <ResendVerificationForm />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <EV91Loader>
            <Layout>
              <Routes>
                <Route path="/" element={<ManagementDashboard />} />

                {/* Department Dashboard Routes */}
                <Route path="/dashboard/operations" element={<OperationsDashboard />} />
                <Route path="/dashboard/sales" element={<SalesDashboard />} />
                <Route path="/dashboard/finance" element={<FinanceDashboard />} />
                <Route path="/war-room" element={<WarRoom />} />
                <Route path="/war-room/charts" element={<WarRoomCharts />} />
                <Route path="/dashboard/management" element={<ManagementDashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/supply" element={<SupplyDashboard />} />
                <Route path="/dashboard/inventory" element={<InventoryDashboard />} />

                <Route path="/employees" element={<Users />} />
                <Route path="/teams" element={<Teams />} />
                <Route path="/teams/create" element={<CreateTeam />} />
                <Route path="/teams/edit/:id" element={<EditTeam />} />
                <Route path="/clients" element={<ClientStoreManagement />} />
                <Route path="/stores" element={<ClientStoreManagement />} />
                <Route path="/rider-earnings" element={<RiderEarnings />} />
                <Route path="/client-rider-mappings" element={<ClientRiderMappingManagement />} />
                <Route path="/rider-management" element={<RiderManagement />} />
                <Route path="/rider-tickets" element={<RiderTickets />} />
                <Route path="/bulk-vehicle-assignment" element={<BulkVehicleAssignmentImport />} />
                <Route path="/rider-management/vehicle-analytics" element={<RiderVehicleAnalytics />} />
                <Route path="/rider-management/attendance-analytics" element={<RiderAttendanceAnalytics />} />
                <Route path="/rider-management/vehicle-history" element={<RiderVehicleHistory />} />
                <Route path="/rider-management/damage-management" element={<Navigate to="/rider-management" replace />} />
                <Route path="/rider-management/:riderId" element={<RiderDetail />} />
                <Route path="/riders/bulk-import" element={<BulkRiderImport />} />
                <Route path="/manual-collections" element={<ManualCollections />} />
                <Route path="/deficit-details" element={<DeficitDetails />} />
                <Route path="/mis" element={<MISManagement />} />
                <Route path="/mis/bulk-upload" element={<MISBulkUploadPage />} />
                <Route path="/mis/history-summary" element={<MISHistorySummary />} />
                <Route path="/payouts" element={<Payouts />} />
                <Route path="/payouts/bulk-upload" element={<PayoutsBulkUpload />} />
                <Route path="/orders" element={<OrderManagement />} />
                <Route path="/order-analytics" element={<OrderAnalytics />} />
                <Route path="/orders/bulk-upload" element={<OrderBulkUpload />} />

                {/* Vendor & Referral Management Routes */}
                <Route path="/vendor-dashboard" element={<VendorDashboard />} />
                <Route path="/vendor-management" element={<VendorManagement />} />
                <Route path="/telecaller-management" element={<TelecallerManagement />} />
                <Route path="/referral-tracking" element={<ReferralTracking />} />
                <Route path="/commission-approval" element={<CommissionApproval />} />

                {/* Vehicle Management Routes */}
                <Route path="/vehicle-dashboard" element={<VehicleDashboard />} />
                <Route path="/vehicles" element={<VehicleInventory />} />
                <Route path="/vehicles/deployment-tickets" element={<DeploymentTicketsPage />} />
                <Route path="/vehicles/return-tickets" element={<ReturnTicketsPage />} />
                <Route path="/vehicles/status-backup" element={<VehicleStatusBackupReport />} />
                <Route path="/vehicles/add" element={<VehicleForm />} />
                <Route path="/vehicles/edit/:id" element={<VehicleForm />} />
                <Route path="/vehicles/view/:id" element={<VehicleProfile />} />
                <Route path="/vehicles/:id" element={<VehicleProfile />} />
                <Route path="/vehicle-profile/:id" element={<VehicleProfile />} />
                <Route path="/damage" element={<DamageManagement />} />
                <Route path="/damage/new" element={<DamageForm />} />
                <Route path="/damage/:id/edit" element={<DamageForm />} />
                <Route path="/vehicles/bulk-import" element={<BulkVehicleImport />} />

                {/* Battery Management Routes */}
                <Route path="/batteries" element={<BatteryInventory />} />

                {/* Charger Management Routes */}
                <Route path="/chargers" element={<ChargerInventory />} />

                {/* Hub Management Routes */}
                <Route path="/hubs" element={<HubManagement />} />
                <Route path="/hubs/new" element={<HubForm />} />
                <Route path="/hubs/:id" element={<HubDetails />} />
                <Route path="/hubs/:id/edit" element={<HubForm />} />

                {/* City Management Routes */}
                <Route path="/cities" element={<CityManagement />} />
                <Route path="/cities/add" element={<CityForm />} />
                <Route path="/cities/:id/edit" element={<CityForm />} />
                <Route path="/city-heads" element={<CityHeadManagement />} />
                <Route path="/field-recruiters" element={<FieldRecruiterManagement />} />
                <Route path="/city-dashboard" element={<CityDashboard />} />

                {/* OEM Management Routes */}
                <Route path="/oems" element={<OEMManagement />} />
                <Route path="/oems/new" element={<OEMForm />} />
                <Route path="/oems/:id/edit" element={<OEMForm />} />

                {/* Vehicle Model Management Routes */}
                <Route path="/vehicle-models" element={<VehicleModelManagement />} />
                <Route path="/vehicle-models/new" element={<VehicleModelForm />} />
                <Route path="/vehicle-models/:id/edit" element={<VehicleModelForm />} />

                {/* Service Management Routes */}
                <Route path="/services" element={<ServiceManagement />} />
                <Route path="/services/schedule" element={<ServiceScheduleForm />} />
                {/* <Route path="/unified-service" element={<UnifiedServiceDashboard />} /> */} {/* DISABLED - Unified Service Management */}

                {/* Order Management Routes - DISABLED */}
                {/* <Route path="/orders" element={<OrderList />} /> */}
                {/* <Route path="/orders/new" element={<OrderForm />} /> */}
                {/* <Route path="/orders/:id/edit" element={<OrderForm />} /> */}
                {/* <Route path="/orders/:id" element={<OrderDetail />} /> */}

                {/* Debug Routes */}
                <Route path="/debug/vehicle-api" element={<DebugVehicleAPI />} />

                {/* API Documentation Route */}
                <Route path="/api-docs" element={<APIDocumentation />} />

                <Route path="/roles" element={<Roles />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/security-deposit-config" element={<SecurityDepositConfigManagement />} />
                <Route path="/security-deposit" element={<SecurityDepositManagement />} />
                <Route path="/paylink-transactions" element={<PayLinkTransactions />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/warroom-summary" element={<WarRoomSummaryPage />} />
                <Route path="/distance-analytics" element={<DistanceAnalytics />} />
                <Route path="/spare-parts" element={<SparePartsDashboard />} />
                <Route path="/spare-parts/categories" element={<CategoriesPage />} />
                <Route path="/spare-parts/suppliers" element={<SuppliersPage />} />
                <Route path="/spare-parts/master" element={<SparePartsPage />} />
                <Route path="/spare-parts/inventory" element={<SparePartsInventoryPage />} />
                <Route path="/spare-parts/procurement" element={<PurchaseBillsPage />} />
                <Route path="/spare-parts/maintenance" element={<SparePartsMaintenancePage />} />
                <Route path="/spare-parts/preventive-maintenance" element={<SparePartsPreventiveMaintenancePage />} />
                <Route path="/spare-parts/analytics" element={<SparePartsAnalyticsPage />} />
                <Route path="/spare-parts/reports" element={<SparePartsReportsPage />} />
                <Route path="/spare-parts/operations" element={<SparePartsOperationsPage />} />
                <Route path="/spare-parts/maintenance-assignments" element={<MaintenanceAssignmentsPage />} />
              </Routes>
            </Layout>
            </EV91Loader>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default App
